package er.prateek.crossly.ui.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavGraphBuilder
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.toRoute
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.share.RiddleChallengeRef
import er.prateek.crossly.data.model.SavedPuzzleSession
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.progression.BrainstormSession
import er.prateek.crossly.ui.screens.crossword.CrosswordScreen
import er.prateek.crossly.ui.screens.crossword.DailyPuzzleScreen
import er.prateek.crossly.ui.screens.home.HomeScreen
import er.prateek.crossly.ui.achievements.AchievementsScreen
import er.prateek.crossly.ui.screens.brainstorm.BrainstormBreakScreen
import er.prateek.crossly.ui.screens.levelselect.LevelSelectScreen
import er.prateek.crossly.ui.screens.math.MathCrosswordScreen
import er.prateek.crossly.ui.screens.math.MathLevelSelectScreen
import er.prateek.crossly.ui.screens.results.ModeResultsScreen
import er.prateek.crossly.ui.screens.results.ResultsScreen
import er.prateek.crossly.ui.screens.riddle.RiddleLevelSelectScreen
import er.prateek.crossly.ui.screens.riddle.RiddleRunScreen
import er.prateek.crossly.ui.gameplay.GameplayState
import er.prateek.crossly.ui.screens.legal.PrivacyPolicyScreen
import er.prateek.crossly.ui.screens.settings.SettingsScreen
import er.prateek.crossly.ui.theme.ThemeState

/**
 * Fires one [AnalyticsEvents.SCREEN_VIEW] per navigation, from a single place, so the funnel
 * across every screen is complete without each screen composable needing to remember to log
 * its own arrival.
 */
private inline fun <reified T : Destination> NavGraphBuilder.trackedComposable(
    screenName: String,
    crossinline content: @Composable (NavBackStackEntry) -> Unit,
) {
    composable<T> { backStackEntry ->
        LaunchedEffect(backStackEntry) {
            AppContainer.analytics.logEvent(AnalyticsEvents.SCREEN_VIEW, mapOf("screen_name" to screenName))
        }
        content(backStackEntry)
    }
}

/**
 * Backstop for the `modes.*.enabled` kill switch: a mode switched off remotely closes on arrival
 * however it was reached (Daily, Brainstorm, a challenge link, a results screen's Next, a restored
 * back stack), not just by hiding its Home tile.
 */
@Composable
private fun ModeGate(
    enabled: Boolean,
    navController: NavHostController,
    entry: NavBackStackEntry,
    content: @Composable () -> Unit,
) {
    if (enabled) {
        content()
        return
    }
    LaunchedEffect(entry) {
        if (navController.currentBackStackEntry?.id == entry.id) navController.popBackStack()
    }
}

/** Advances the Brainstorm run past a finished game and builds its break-screen route. */
private fun brainstormBreak(mode: GameMode, stars: Int): Destination.BrainstormBreak {
    val roundComplete = BrainstormSession.onGameFinished()
    if (roundComplete) AppContainer.playerJourney.onBrainstormRoundCompleted()
    return Destination.BrainstormBreak(mode.name, stars, roundComplete)
}

@Composable
fun AppNavHost(
    navController: NavHostController,
    themeState: ThemeState,
    gameplayState: GameplayState,
) {
    val scope = rememberCoroutineScope()
    NavHost(navController = navController, startDestination = Destination.Home) {
        trackedComposable<Destination.Home>("home") {
            HomeScreen(
                onPlay = { navController.navigate(Destination.LevelSelect(DifficultyTier.EASY.name)) },
                onPlayDaily = { navController.navigate(Destination.Daily) },
                onPlayMath = { navController.navigate(Destination.MathLevelSelect(DifficultyTier.EASY.name)) },
                onPlayRiddles = { navController.navigate(Destination.RiddleLevelSelect(DifficultyTier.EASY.name)) },
                onPlayBrainstorm = {
                    BrainstormSession.start()
                    AppContainer.analytics.logEvent(AnalyticsEvents.BRAINSTORM_STARTED)
                    scope.launch { navController.navigate(BrainstormSession.nextDestination()) }
                },
                onOpenAchievements = { navController.navigate(Destination.Achievements) },
                onOpenSettings = { navController.navigate(Destination.Settings) },
                onResume = { session ->
                    if (session.isDaily) {
                        navController.navigate(Destination.Daily)
                    } else {
                        navController.navigate(
                            Destination.Crossword(
                                puzzleId = session.puzzleId,
                                fromChallenge = session.fromChallenge,
                                challengerName = session.challengerName,
                                challengerTimeMs = session.challengerTimeMs,
                            ),
                        )
                    }
                },
            )
        }
        trackedComposable<Destination.Settings>("settings") {
            SettingsScreen(
                themeState = themeState,
                gameplayState = gameplayState,
                onBack = { navController.popBackStack() },
                onOpenPrivacyPolicy = { navController.navigate(Destination.PrivacyPolicy) },
            )
        }
        trackedComposable<Destination.PrivacyPolicy>("privacy_policy") {
            PrivacyPolicyScreen(onBack = { navController.popBackStack() })
        }
        trackedComposable<Destination.LevelSelect>("level_select") { backStackEntry ->
            val route: Destination.LevelSelect = backStackEntry.toRoute()
            LevelSelectScreen(
                startTier = DifficultyTier.valueOf(route.startTier),
                onOpenPuzzle = { puzzleId -> navController.navigate(Destination.Crossword(puzzleId)) },
                onBack = { navController.popBackStack() },
            )
        }
        trackedComposable<Destination.Crossword>("crossword") { backStackEntry ->
            val route: Destination.Crossword = backStackEntry.toRoute()
            CrosswordScreen(
                puzzleId = route.puzzleId,
                gameplayState = gameplayState,
                onBack = { navController.popBackStack() },
                fromChallenge = route.fromChallenge,
                challengerName = route.challengerName,
                challengerTimeMs = route.challengerTimeMs,
                onSolved = { puzzleId, stars, elapsedMs ->
                    if (route.fromChallenge) {
                        AppContainer.shareManager.markCompletedIfMatching(puzzleId)
                    }
                    navController.popBackStack()
                    if (BrainstormSession.active) {
                        navController.navigate(brainstormBreak(GameMode.CROSSWORD, stars))
                        return@CrosswordScreen
                    }
                    navController.navigate(
                        Destination.Results(
                            puzzleId = puzzleId,
                            stars = stars,
                            fromChallenge = route.fromChallenge,
                            challengerName = route.challengerName,
                            elapsedMs = elapsedMs,
                            challengerTimeMs = route.challengerTimeMs,
                        ),
                    )
                },
            )
        }
        trackedComposable<Destination.Daily>("daily") {
            DailyPuzzleScreen(
                gameplayState = gameplayState,
                onBack = { navController.popBackStack() },
                onPlayMath = { levelId ->
                    navController.popBackStack()
                    navController.navigate(Destination.MathCrossword(levelId, isDaily = true))
                },
                onPlayRiddles = { levelId ->
                    navController.popBackStack()
                    navController.navigate(Destination.RiddleRun(levelId, isDaily = true))
                },
                onSolved = { puzzleId, stars, elapsedMs ->
                    navController.popBackStack()
                    navController.navigate(
                        Destination.Results(puzzleId, stars, isDaily = true, elapsedMs = elapsedMs),
                    )
                },
            )
        }
        trackedComposable<Destination.MathLevelSelect>("math_level_select") { backStackEntry ->
            val route: Destination.MathLevelSelect = backStackEntry.toRoute()
            ModeGate(AppContainer.appConfig().modes.mathCrossword.enabled, navController, backStackEntry) {
                MathLevelSelectScreen(
                    startTier = DifficultyTier.valueOf(route.startTier),
                    onOpenLevel = { levelId -> navController.navigate(Destination.MathCrossword(levelId)) },
                    onBack = { navController.popBackStack() },
                )
            }
        }
        trackedComposable<Destination.MathCrossword>("math_crossword") { backStackEntry ->
            val route: Destination.MathCrossword = backStackEntry.toRoute()
            ModeGate(AppContainer.appConfig().modes.mathCrossword.enabled, navController, backStackEntry) {
                MathCrosswordScreen(
                    levelId = route.levelId,
                    isDaily = route.isDaily,
                    onBack = { navController.popBackStack() },
                    onSolved = { levelId, stars, elapsedMs ->
                        navController.popBackStack()
                        if (BrainstormSession.active) {
                            navController.navigate(brainstormBreak(GameMode.MATH_CROSSWORD, stars))
                            return@MathCrosswordScreen
                        }
                        navController.navigate(
                            Destination.ModeResults(GameMode.MATH_CROSSWORD.name, levelId, stars, elapsedMs),
                        )
                    },
                )
            }
        }
        trackedComposable<Destination.RiddleLevelSelect>("riddle_level_select") { backStackEntry ->
            val route: Destination.RiddleLevelSelect = backStackEntry.toRoute()
            ModeGate(AppContainer.appConfig().modes.riddles.enabled, navController, backStackEntry) {
                RiddleLevelSelectScreen(
                    startTier = DifficultyTier.valueOf(route.startTier),
                    onOpenLevel = { levelId -> navController.navigate(Destination.RiddleRun(levelId)) },
                    onBack = { navController.popBackStack() },
                )
            }
        }
        trackedComposable<Destination.RiddleRun>("riddle_run") { backStackEntry ->
            val route: Destination.RiddleRun = backStackEntry.toRoute()
            ModeGate(AppContainer.appConfig().modes.riddles.enabled, navController, backStackEntry) {
                RiddleRunScreen(
                    levelId = route.levelId,
                    isDaily = route.isDaily,
                    seed = route.seed,
                    challengerName = route.challengerName.takeIf { route.fromChallenge },
                    onBack = { navController.popBackStack() },
                    onFinished = { result ->
                        navController.popBackStack()
                        if (route.fromChallenge) {
                            AppContainer.shareManager.markCompletedIfMatching(RiddleChallengeRef(result.levelId, result.seed).puzzleId)
                        }
                        // A challenge always ends on the results screen, where it is won or lost. So
                        // does a failed set: it doesn't count as a Brainstorm game, and the results
                        // screen's Play Again retries it with the run still going.
                        if (BrainstormSession.active && !route.fromChallenge && result.stars > 0) {
                            navController.navigate(brainstormBreak(GameMode.RIDDLES, result.stars))
                            return@RiddleRunScreen
                        }
                        navController.navigate(
                            Destination.ModeResults(
                                mode = GameMode.RIDDLES.name,
                                levelId = result.levelId,
                                stars = result.stars,
                                elapsedMs = result.elapsedMs,
                                seed = result.seed,
                                correct = result.correct,
                                total = result.total,
                                passMark = result.passMark,
                                fromChallenge = route.fromChallenge,
                                challengerName = route.challengerName,
                                challengerTimeMs = route.challengerTimeMs,
                                challengerScore = route.challengerScore,
                            ),
                        )
                    },
                )
            }
        }
        trackedComposable<Destination.ModeResults>("mode_results") { backStackEntry ->
            val route: Destination.ModeResults = backStackEntry.toRoute()
            val playDestination: (String) -> Destination = { lid ->
                if (route.mode == GameMode.RIDDLES.name) Destination.RiddleRun(lid) else Destination.MathCrossword(lid)
            }
            ModeResultsScreen(
                mode = route.mode,
                levelId = route.levelId,
                stars = route.stars,
                elapsedMs = route.elapsedMs,
                seed = route.seed,
                correct = route.correct,
                total = route.total,
                passMark = route.passMark,
                challengerName = route.challengerName.takeIf { route.fromChallenge },
                challengerTimeMs = route.challengerTimeMs,
                challengerScore = route.challengerScore,
                onReplay = { lid -> navController.popBackStack(); navController.navigate(playDestination(lid)) },
                onNextLevel = { lid -> navController.popBackStack(); navController.navigate(playDestination(lid)) },
                onBackToLevels = { navController.popBackStack() },
            )
        }
        trackedComposable<Destination.BrainstormBreak>("brainstorm_break") { backStackEntry ->
            val route: Destination.BrainstormBreak = backStackEntry.toRoute()
            BrainstormBreakScreen(
                completedMode = GameMode.valueOf(route.completedMode),
                stars = route.stars,
                roundComplete = route.roundComplete,
                onContinue = {
                    scope.launch {
                        val next = BrainstormSession.nextDestination()
                        navController.popBackStack()
                        navController.navigate(next)
                    }
                },
                onEnd = {
                    BrainstormSession.stop(reason = "end_button")
                    navController.popBackStack()
                },
            )
        }
        trackedComposable<Destination.Achievements>("achievements") {
            AchievementsScreen(onBack = { navController.popBackStack() })
        }
        trackedComposable<Destination.Results>("results") { backStackEntry ->
            val route: Destination.Results = backStackEntry.toRoute()
            ResultsScreen(
                puzzleId = route.puzzleId,
                stars = route.stars,
                isDaily = route.isDaily,
                fromChallenge = route.fromChallenge,
                challengerName = route.challengerName,
                elapsedMs = route.elapsedMs,
                challengerTimeMs = route.challengerTimeMs,
                onPlayAgain = { puzzleId ->
                    navController.popBackStack()
                    navController.navigate(
                        Destination.Crossword(
                            puzzleId = puzzleId,
                            fromChallenge = route.fromChallenge,
                            challengerName = route.challengerName,
                            challengerTimeMs = route.challengerTimeMs,
                        ),
                    )
                },
                onNextLevel = { puzzleId ->
                    navController.popBackStack()
                    navController.navigate(Destination.Crossword(puzzleId))
                },
                // Results replaced Crossword/Daily in place, so the stack is
                // Home -> LevelSelect -> Results (one pop -> LevelSelect) for
                // level play, or Home -> Results (one pop -> Home) for Daily.
                onBackToLevels = { navController.popBackStack() },
            )
        }
    }
}
